/**
 * unitEconomicsEngine.js — pure calculation functions for unit economics.
 *
 * Input variables (editable on the admin page):
 *   - tiers[]: { name, price, infraCost, retentionMonths, mixPct }
 *   - global:  { cac, totalSubs, monthlyOpex, startingCash, month }
 *
 * Output: fully derived headline metrics, tier rows, and weighted averages
 * that are displayed on the investor pitch deck.
 */

// ── Default input variables ──────────────────────────────────────────────────

export const DEFAULT_INPUTS = {
  month: 12,
  totalSubs: 1224,
  cac: 80,
  monthlyOpex: 45000,
  startingCash: 250000,
  currency: "A$",
  tiers: [
    { name: "Starter",  price: 65,  infraCost: 27, retentionMonths: 12, mixPct: 55 },
    { name: "Pro",      price: 150, infraCost: 55, retentionMonths: 18, mixPct: 35 },
    { name: "Premium",  price: 350, infraCost: 90, retentionMonths: 24, mixPct: 10 },
  ],
};

// ── Calculation helpers ──────────────────────────────────────────────────────

function fmtCurrency(value, currency) {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return `${currency}${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000)     return `${currency}${(value / 1_000).toFixed(1)}K`;
  return `${currency}${value.toFixed(0)}`;
}

function fmtCurrencyExact(value, currency) {
  return `${currency}${value.toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
}

function fmtMultiple(value) {
  return `${value.toFixed(1)}×`;
}

function fmtMonths(value) {
  return `${value.toFixed(1)} mo`;
}

// ── Core calculation ─────────────────────────────────────────────────────────

/**
 * Given editable inputs, compute the full set of derived unit-economics data.
 * Returns the same shape the UnitEconomicsSection expects.
 */
export function computeUnitEconomics(inputs) {
  const { tiers, totalSubs, cac, monthlyOpex, startingCash, currency, month } = inputs;

  // Normalise mix percentages so they always sum to 100
  const rawMixSum = tiers.reduce((s, t) => s + t.mixPct, 0);
  const normFactor = rawMixSum > 0 ? 100 / rawMixSum : 0;

  // ── Per-tier calculations ──
  const tierRows = tiers.map((t) => {
    const normMix = t.mixPct * normFactor;          // normalised %
    const gm = t.price - t.infraCost;               // gross margin $/mo
    const ltv = gm * t.retentionMonths;              // lifetime value (GM-based)
    const ltvCac = cac > 0 ? ltv / cac : 0;          // LTV:CAC multiple
    const subs = Math.round((normMix / 100) * totalSubs);

    return {
      name: t.name,
      price: t.price,
      infraCost: t.infraCost,
      gm,
      retentionMonths: t.retentionMonths,
      ltv,
      ltvCac,
      normMix,
      subs,
    };
  });

  // ── Weighted averages ──
  const weightedArpu = tierRows.reduce((s, t) => s + (t.normMix / 100) * t.price, 0);
  const weightedGm   = tierRows.reduce((s, t) => s + (t.normMix / 100) * t.gm, 0);
  const weightedLtv  = tierRows.reduce((s, t) => s + (t.normMix / 100) * t.ltv, 0);
  const weightedLtvCac = cac > 0 ? weightedLtv / cac : 0;

  // ── Headline metrics ──
  const mrr           = tierRows.reduce((s, t) => s + t.subs * t.price, 0);
  const totalGrossMargin = tierRows.reduce((s, t) => s + t.subs * t.gm, 0);
  const paybackMonths = weightedGm > 0 ? cac / weightedGm : 0;
  const netMonthly    = totalGrossMargin - monthlyOpex;
  const cashPosition  = startingCash + (netMonthly * month);

  // ── Format for display ──
  const headline = {
    subs: totalSubs.toLocaleString("en-AU"),
    mrr: fmtCurrency(mrr, currency),
    grossMargin: fmtCurrency(totalGrossMargin, currency),
    cac: `${currency}${cac}`,
    payback: fmtMonths(paybackMonths),
    ltvCac: fmtMultiple(weightedLtvCac),
    netMonthly: `${netMonthly >= 0 ? "+" : ""}${fmtCurrency(netMonthly, currency)}/m`,
    cashPosition: fmtCurrency(cashPosition, currency),
  };

  const displayTiers = tierRows.map((t) => ({
    tier: t.name,
    price: `${currency}${t.price}`,
    infra: `${currency}${t.infraCost}`,
    gm: `${currency}${t.gm}`,
    retention: `${t.retentionMonths} mo`,
    ltv: fmtCurrencyExact(t.ltv, currency),
    ltvCac: fmtMultiple(t.ltvCac),
  }));

  return {
    headline,
    tiers: displayTiers,
    weightedArpu: `${currency}${weightedArpu.toFixed(2)}`,
    weightedGm: `${currency}${weightedGm.toFixed(2)}`,
    weightedLtv: `${fmtCurrencyExact(Math.round(weightedLtv), currency)} (≈${fmtMultiple(weightedLtvCac)} CAC)`,

    // Raw numbers for admin display
    _raw: {
      mrr,
      totalGrossMargin,
      paybackMonths,
      netMonthly,
      cashPosition,
      weightedArpu,
      weightedGm,
      weightedLtv,
      weightedLtvCac,
      tierRows,
    },
  };
}
